import { RECORDING_PROVIDERS } from '../../../providerIds'
import {
  queryAgoraSessionRecordingStatus,
  startAgoraSessionRecording,
  stopAgoraSessionRecording,
} from 'src/consumer/services/featureServices'
import {
  createRecordingArtifactManifest,
  createRecordingSessionMetadata,
  RECORDING_SESSION_STATES,
} from '../recordingMetadata'

function stringifyValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    return value
  }

  try {
    return JSON.stringify(value)
  } catch (error) {
    return String(value)
  }
}

function resolveResponseData(response) {
  const envelope = response?.data || response?.response?.data || {}

  if (
    envelope &&
    typeof envelope === 'object' &&
    !Array.isArray(envelope) &&
    'data' in envelope &&
    envelope.data &&
    typeof envelope.data === 'object' &&
    !Array.isArray(envelope.data)
  ) {
    return envelope.data
  }

  return envelope
}

function resolveResponseStatus(response) {
  return response?.status || response?.response?.status || null
}

function resolveEnvelopeCode(response) {
  const envelope = response?.data || response?.response?.data || {}
  return envelope?.code ?? null
}

function getErrorMessage(response, fallbackValue) {
  return (
    response?.response?.data?.message ||
    response?.data?.message ||
    response?.message ||
    fallbackValue
  )
}

function isSuccessfulResponse(response) {
  const status = resolveResponseStatus(response)
  const envelopeCode = resolveEnvelopeCode(response)

  if (envelopeCode !== null) {
    return status === 200 && envelopeCode === 2000
  }

  return status === 200
}

function normalizeAgoraRecordingStatus(response, fallbackStatus) {
  const responseData = resolveResponseData(response)
  const normalizedStatus =
    responseData?.recordingSession?.status || responseData?.recordingStatus || null
  const serverStatus = responseData?.serverResponse?.status
  const activeRecordingFlag = responseData?.serverResponse?.statusDetails?.recording

  if (Object.values(RECORDING_SESSION_STATES).includes(normalizedStatus)) {
    return normalizedStatus
  }

  if (activeRecordingFlag === 1 || activeRecordingFlag === '1') {
    return RECORDING_SESSION_STATES.recording
  }

  if (serverStatus === 1 || serverStatus === '1') {
    return RECORDING_SESSION_STATES.recording
  }

  if (serverStatus === 0 || serverStatus === '0') {
    return RECORDING_SESSION_STATES.stopped
  }

  return fallbackStatus
}

function buildAgoraArtifactManifest(responseData, channelName, fallbackRecordingPath = null) {
  if (responseData?.recordingSession?.artifactManifest) {
    return createRecordingArtifactManifest(responseData.recordingSession.artifactManifest)
  }

  if (responseData?.artifactManifest) {
    return createRecordingArtifactManifest(responseData.artifactManifest)
  }

  const rawFileList =
    responseData?.serverResponse?.statusDetails?.recordingFileList ||
    responseData?.serverResponse?.fileList

  return createRecordingArtifactManifest({
    storageProvider: RECORDING_PROVIDERS.agoraCloud,
    pathPrefix: channelName ? `recordings/${channelName}` : null,
    recordingPath: responseData?.recordingPath || responseData?.serverResponse?.fileList || fallbackRecordingPath,
    rawFileList: stringifyValue(rawFileList),
  })
}

function buildAgoraRecordingSessionMetadata({
  bookedEventId,
  uidRecording,
  resourceId,
  sid,
  response,
  fallbackStatus,
}) {
  const responseData = resolveResponseData(response)
  const normalizedRecordingSession = responseData?.recordingSession || null
  const providerResourceId =
    normalizedRecordingSession?.providerResourceId ||
    responseData?.providerResourceId ||
    responseData?.resourceId ||
    resourceId
  const providerRecordingId =
    normalizedRecordingSession?.providerRecordingId ||
    responseData?.providerRecordingId ||
    responseData?.sid ||
    sid
  const channelName =
    normalizedRecordingSession?.channelName || responseData?.channelName || responseData?.cname || null

  return createRecordingSessionMetadata({
    provider:
      normalizedRecordingSession?.provider ||
      responseData?.recordingProvider ||
      RECORDING_PROVIDERS.agoraCloud,
    bookedEventId,
    recordingSessionId:
      normalizedRecordingSession?.recordingSessionId ||
      responseData?.recordingSessionId ||
      responseData?.recordingId ||
      providerRecordingId,
    providerRecordingId,
    providerResourceId,
    providerUid:
      normalizedRecordingSession?.providerUid ||
      responseData?.providerUid ||
      responseData?.uid ||
      uidRecording,
    channelName,
    status: normalizeAgoraRecordingStatus(response, fallbackStatus),
    startedAt:
      normalizedRecordingSession?.recordingStartedAt ||
      normalizedRecordingSession?.startedAt ||
      responseData?.recordingStartedAt ||
      responseData?.startedAt ||
      null,
    stoppedAt:
      normalizedRecordingSession?.recordingStoppedAt ||
      normalizedRecordingSession?.stoppedAt ||
      responseData?.recordingStoppedAt ||
      responseData?.stoppedAt ||
      null,
    artifactManifest: buildAgoraArtifactManifest(responseData, channelName),
  })
}

export function createAgoraCloudRecordingAdapter() {
  return {
    id: RECORDING_PROVIDERS.agoraCloud,
    domain: 'recording',
    async start({ bookedEventId, uidRecording }) {
      const response = await startAgoraSessionRecording(
        bookedEventId,
        uidRecording,
        RECORDING_PROVIDERS.agoraCloud,
      )
      const success = isSuccessfulResponse(response)

      return {
        success,
        status: resolveResponseStatus(response),
        error: success ? null : getErrorMessage(response, 'Failed to start recording'),
        recordingSession: success
          ? buildAgoraRecordingSessionMetadata({
              bookedEventId,
              uidRecording,
              response,
              fallbackStatus: RECORDING_SESSION_STATES.recording,
            })
          : null,
        rawData: resolveResponseData(response),
      }
    },
    async stop({ bookedEventId, uidRecording, resourceId, sid }) {
      const response = await stopAgoraSessionRecording(
        bookedEventId,
        uidRecording,
        resourceId,
        sid,
        RECORDING_PROVIDERS.agoraCloud,
      )
      const success = isSuccessfulResponse(response)

      return {
        success,
        status: resolveResponseStatus(response),
        error: success ? null : getErrorMessage(response, 'Failed to stop recording'),
        recordingSession: success
          ? buildAgoraRecordingSessionMetadata({
              bookedEventId,
              uidRecording,
              resourceId,
              sid,
              response,
              fallbackStatus: RECORDING_SESSION_STATES.stopped,
            })
          : null,
        rawData: resolveResponseData(response),
      }
    },
    async query({ bookedEventId, uidRecording, resourceId, sid }) {
      const response = await queryAgoraSessionRecordingStatus(
        bookedEventId,
        resourceId,
        sid,
        RECORDING_PROVIDERS.agoraCloud,
      )
      const success = isSuccessfulResponse(response)

      return {
        success,
        status: resolveResponseStatus(response),
        error: success ? null : getErrorMessage(response, 'Failed to query recording status'),
        recordingSession: success
          ? buildAgoraRecordingSessionMetadata({
              bookedEventId,
              uidRecording,
              resourceId,
              sid,
              response,
              fallbackStatus: RECORDING_SESSION_STATES.unknown,
            })
          : null,
        rawData: resolveResponseData(response),
      }
    },
  }
}

export const agoraCloudRecordingAdapter = createAgoraCloudRecordingAdapter()
